import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Search, 
  Filter, 
  ExternalLink,
  ChevronRight,
  Building2,
  Mail,
  Phone,
  Calendar,
  Ban,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  sourced: { label: "Sourced", className: "status-badge bg-muted text-muted-foreground" },
  generating: { label: "Generating", className: "status-info" },
  preview_ready: { label: "Preview Ready", className: "status-info" },
  email_sent: { label: "Email Sent", className: "status-success" },
  call_booked: { label: "Call Booked", className: "status-warning" },
  won: { label: "Won", className: "status-success" },
  lost: { label: "Lost", className: "status-error" }
};

const FastActionBadge = ({ expiresAt }) => {
  if (!expiresAt) return null;
  
  const now = new Date();
  const expires = new Date(expiresAt);
  const isExpired = expires < now;
  const hoursLeft = Math.max(0, Math.floor((expires - now) / (1000 * 60 * 60)));
  
  if (isExpired) {
    return (
      <span className="text-xs text-red-500 flex items-center gap-1">
        <Clock size={12} />
        Expired
      </span>
    );
  }
  
  return (
    <span className="text-xs text-yellow-600 flex items-center gap-1">
      <Clock size={12} />
      {hoursLeft}h left
    </span>
  );
};

export default function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [skippedLeads, setSkippedLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, skippedRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/leads?limit=200`),
        axios.get(`${API}/leads/skipped?limit=100`),
        axios.get(`${API}/campaigns`)
      ]);
      setLeads(leadsRes.data);
      setSkippedLeads(skippedRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = (leadsList) => {
    return leadsList.filter(lead => {
      const matchesSearch = lead.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           lead.city.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
      const matchesCampaign = campaignFilter === "all" || lead.campaign_id === campaignFilter;
      return matchesSearch && matchesStatus && matchesCampaign;
    });
  };

  const filteredLeads = filterLeads(leads);
  const filteredSkipped = filterLeads(skippedLeads);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="leads-list-page">
      <div className="page-header">
        <h1 className="page-title">Leads</h1>
        <p className="page-subtitle">Manage and track all sourced business leads</p>
      </div>
      
      <div className="page-content">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input 
              data-testid="search-input"
              placeholder="Search by business name or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusConfig).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-[200px]" data-testid="campaign-filter">
              <SelectValue placeholder="All Campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map(c => (
                <SelectItem key={c.campaign_id} value={c.campaign_id}>
                  {c.category} - {c.city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="active" data-testid="active-tab">
              Active Leads ({filteredLeads.length})
            </TabsTrigger>
            <TabsTrigger value="skipped" data-testid="skipped-tab">
              Skipped ({filteredSkipped.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <Card>
              <CardContent className="p-0">
                {filteredLeads.length === 0 ? (
                  <div className="empty-state">
                    <Building2 className="empty-state-icon" />
                    <h3 className="empty-state-title">No leads found</h3>
                    <p className="empty-state-text">
                      {leads.length === 0 
                        ? "Start a campaign to generate leads"
                        : "Try adjusting your filters"}
                    </p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Business</th>
                        <th>Contact</th>
                        <th>Location</th>
                        <th>Industry</th>
                        <th>Status</th>
                        <th>Fast-Action</th>
                        <th>Preview</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.map((lead) => {
                        const status = statusConfig[lead.status] || statusConfig.sourced;
                        return (
                          <tr 
                            key={lead.lead_id} 
                            className="table-row-hover"
                            data-testid={`lead-row-${lead.lead_id}`}
                          >
                            <td>
                              <div className="font-medium">{lead.business_name}</div>
                            </td>
                            <td>
                              <div className="space-y-1 text-sm">
                                {lead.email && (
                                  <div className="flex items-center gap-1.5 text-muted-foreground">
                                    <Mail size={14} />
                                    <span className="truncate max-w-[180px]">{lead.email}</span>
                                  </div>
                                )}
                                {lead.phone && (
                                  <div className="flex items-center gap-1.5 text-muted-foreground">
                                    <Phone size={14} />
                                    <span>{lead.phone}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="text-muted-foreground">
                              {lead.city}, {lead.state}
                            </td>
                            <td>{lead.primary_industry || lead.category}</td>
                            <td>
                              <Badge className={status.className}>
                                {status.label}
                              </Badge>
                              {lead.appointment_datetime && (
                                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                                  <Calendar size={12} />
                                  {new Date(lead.appointment_datetime).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td>
                              <FastActionBadge expiresAt={lead.fast_action_expires_at} />
                            </td>
                            <td>
                              {lead.preview_url ? (
                                <a 
                                  href={`${process.env.REACT_APP_BACKEND_URL}${lead.preview_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-accent hover:underline text-sm"
                                >
                                  <ExternalLink size={14} />
                                  View
                                </a>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </td>
                            <td>
                              <Link to={`/admin/leads/${lead.lead_id}`}>
                                <Button variant="ghost" size="sm" data-testid={`view-lead-${lead.lead_id}`}>
                                  <ChevronRight size={18} />
                                </Button>
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="skipped">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Ban size={18} className="text-yellow-500" />
                  Skipped Leads (Already Contacted)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {filteredSkipped.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No skipped leads. Deduplication is working correctly!</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Business</th>
                        <th>Location</th>
                        <th>Industry</th>
                        <th>Skip Reason</th>
                        <th>Original Campaign</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSkipped.map((lead) => (
                        <tr 
                          key={lead.lead_id} 
                          className="table-row-hover opacity-70"
                          data-testid={`skipped-lead-${lead.lead_id}`}
                        >
                          <td>
                            <div className="font-medium">{lead.business_name}</div>
                            {lead.email && (
                              <div className="text-xs text-muted-foreground">{lead.email}</div>
                            )}
                          </td>
                          <td className="text-muted-foreground">
                            {lead.city}, {lead.state}
                          </td>
                          <td>{lead.primary_industry || lead.category}</td>
                          <td>
                            <Badge className="status-warning">
                              {lead.suppression_reason || "Already contacted"}
                            </Badge>
                          </td>
                          <td className="text-xs text-muted-foreground">
                            {lead.suppressed_from_campaign_id ? 
                              lead.suppressed_from_campaign_id.slice(0, 8) + "..." : 
                              "—"}
                          </td>
                          <td>
                            <Link to={`/admin/leads/${lead.lead_id}`}>
                              <Button variant="ghost" size="sm">
                                <ChevronRight size={18} />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
